import * as React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { color, theme, variants, media } from 'styles/utils';
import Link from 'components/Link';

const Button = styled(
  React.forwardRef(({ disabled, href, to, variant, ...rest }, ref) => {
    const isLink = href || to;
    const props = {
      ...rest,
      ref,
      disabled,
      href,
      to,
    };
    return isLink ? <Link {...props} /> : <button {...props} />;
  })
)`
  align-items: center;
  color: inherit;
  border-radius: 0.25rem;
  display: inline-flex;
  font-family: ${theme('fonts.button')};
  font-weight: bold;
  line-height: 1.25;
  justify-content: center;
  letter-spacing: 0.02em;
  text-decoration: none;
  white-space: nowrap;
  transition: filter 150ms ease-in-out;

  &:disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  ${media.hover} {
    &:hover {
      filter: brightness(1.1);
    }
  }

  &:focus-visible {
    outline: 0.25em solid ${color('teal')};
    outline-offset: 0.25em;
  }

  ${({ fill }) =>
    fill &&
    css`
      filter: none !important;

      &::after {
        block-size: 100%;
        content: '';
        inline-size: 100%;
        left: 0;
        position: absolute;
        top: 0;
        z-index: 1;
      }
    `}

  ${({ size }) =>
    ({
      sm: css`
        padding: 0.5rem 1rem;
        font-size: 0.875rem;
      `,
      md: css`
        padding: 0.75rem 2rem;
        font-size: 1rem;
      `,
    }[size])}

  ${variants({
    primary: css`
      background-color: ${color('yellow')};
      border: 1px solid ${color('yellow')};
      color: ${color('darkTeal')};
    `,
    secondaryLight: css`
      border: 1px solid ${color('skyBlue')};
      color: ${color('white')};
    `,
    secondary: css`
      border: 1px solid ${color('teal')};
      color: ${color('white')};
    `,
    tertiary: css`
      background-color: ${color('teal')};
      border: 1px solid ${color('teal')};
      color: ${color('white')};
    `,
    quaternary: css`
      background-color: ${color('white')};
      border: 1px solid ${color('textGray')};
      color: ${color('teal')};
    `,
    text: css`
      border: 1px solid transparent;
      font: inherit;
      letter-spacing: normal;
    `,
    tag: css`
      background-color: ${color('teal')};
      border-radius: 0.5rem;
      border: 1px solid ${color('teal')};
      color: ${color('white')};
      &::before {
        content: '';
        inset: 0;
        position: relative;
        width: 0.5rem;
        height: 0.5rem;
        border-radius: 100%;
        background-color: ${color('orange')};
        margin-right: 0.75rem;
      }
    `,
    navItem: css`
      display: block;
      font-weight: normal;
      padding: 0.75rem;

      &[data-depth]:not([data-depth='0']) {
        padding: 0.5rem;
      }
    `,
  })}
`;

Button.propTypes = {
  children: PropTypes.any,
  disabled: PropTypes.bool,
  fill: PropTypes.bool,
  href: PropTypes.string,
  size: PropTypes.oneOf(['sm', 'md']),
  to: PropTypes.string,
  variant: PropTypes.oneOf([
    'primary',
    'secondary',
    'secondaryLight',
    'text',
    'tertiary',
    'quaternary',
    'tag',
    'navItem',
  ]),
};

Button.defaultProps = {
  children: null,
  disabled: false,
  href: undefined,
  size: 'md',
  to: undefined,
  variant: 'primary',
};

export default Button;
